import {
  Box,
  CheckboxPillGroup,
  Column,
  InputNumberField,
  InputSelectField,
  Row,
  Typography,
  type CheckboxPillOption,
} from '@tempmee/react';
import type { OptionValue } from '@tempmee/core';
import { colors } from '@tempmee/tailwind';
import { useMemo, type ReactNode } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { numberOfOperatoriesOptions } from '~/components/registration/utils';
import {
  COST_UNIT_OPTIONS,
  PARKING_COST_PATTERN,
  TIME_PER_PATIENT_OPTIONS,
  YES_NO_OPTIONS,
} from '~/constants/officeProfileOptions';
import {
  isParkingAsked,
  NOT_ANSWERED,
  OFFICE_MISSING_INFO_COPY as COPY,
  type IOfficeMissingInfoFormValues,
  type TMissingOfficeInfoField,
} from '~/domain/OfficeMissingInfo';
import { useParkingTypeOptions } from '~/hooks/useParkingTypeOptions';
import { SoftwareUtils } from '~/utils/SoftwareUtils';
import { type IOfficeMissingInfoCatalog } from './useOfficeMissingInfoCatalog';

// The modal never offers 'N/A': a stored 'N/A' is what makes the field missing.
const OPERATORIES_OPTIONS = (numberOfOperatoriesOptions as string[])
  .filter((value) => value !== NOT_ANSWERED)
  .map((value) => ({ label: value, value }));

const toYesNo = (value: boolean | null): string | undefined => {
  if (value === true) return 'yes';
  if (value === false) return 'no';
  return undefined;
};

interface ISectionProps {
  overline: string;
  helper?: string;
  dataCy: string;
  children: ReactNode;
}

const Section = ({ overline, helper, dataCy, children }: ISectionProps) => (
  <Column gap={2} pt={4} data-cy={dataCy}>
    <Typography variant="overline" color={colors.text.primary}>
      {overline}
    </Typography>
    {helper && (
      <Typography variant="body1" color={colors.text.secondary}>
        {helper}
      </Typography>
    )}
    {children}
  </Column>
);

type TSelectFieldName = 'timePerPatient' | 'parkingType' | 'parkingCostType' | 'operatories';

interface ISelectFieldProps {
  name: TSelectFieldName;
  label: string;
  options: OptionValue<string>[];
  dataCy: string;
  /** Grow to share a row with a sibling field. */
  flex?: boolean;
}

const SelectField = ({ name, label, options, dataCy, flex }: ISelectFieldProps) => {
  const { control } = useFormContext<IOfficeMissingInfoFormValues>();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Box flex={flex} data-cy={dataCy}>
          <InputSelectField<string>
            label={label}
            options={options}
            value={field.value || undefined}
            onChange={field.onChange}
            error={Boolean(fieldState.error)}
          />
        </Box>
      )}
    />
  );
};

interface IYesNoFieldProps {
  name: 'isAssistedHygiene' | 'isParkingFree';
  label: string;
  dataCy: string;
}

/** A Yes / No select over a boolean form value; unanswered is `null`. */
const YesNoField = ({ name, label, dataCy }: IYesNoFieldProps) => {
  const { control } = useFormContext<IOfficeMissingInfoFormValues>();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Box data-cy={dataCy}>
          <InputSelectField<string>
            label={label}
            options={YES_NO_OPTIONS}
            value={toYesNo(field.value)}
            onChange={(value) => field.onChange(value === 'yes')}
            error={Boolean(fieldState.error)}
          />
        </Box>
      )}
    />
  );
};

interface IOfficeMissingInfoFieldsProps {
  /** Only these fields render, in this order. */
  missingFields: TMissingOfficeInfoField[];
  /** Reduced profession code, shown as the time-per-patient label. */
  professionCode: string;
  catalog: IOfficeMissingInfoCatalog;
}

export const OfficeMissingInfoFields = ({
  missingFields,
  professionCode,
  catalog,
}: IOfficeMissingInfoFieldsProps) => {
  const { control } = useFormContext<IOfficeMissingInfoFormValues>();
  const isParkingFree = useWatch({ control, name: 'isParkingFree' });

  const isAsked = (field: TMissingOfficeInfoField) => missingFields.includes(field);

  const parkingTypeOptions = useParkingTypeOptions();

  // Pills show the translated name ("DOX") but carry the catalog name the BE expects.
  const softwareOptions = useMemo<CheckboxPillOption[]>(
    () =>
      catalog.softwares.map((name) => ({
        label: SoftwareUtils.getSoftwareTranslations([name])[0],
        value: name,
      })),
    [catalog.softwares],
  );

  const insuranceOptions = useMemo<CheckboxPillOption[]>(
    () => catalog.insurances.map((name) => ({ label: name, value: name })),
    [catalog.insurances],
  );

  const showCostFields = isAsked('parkingCost') && isParkingFree === false;
  const showCostUnitOnly = !isAsked('parkingCost') && isAsked('parkingCostType');

  const costUnitField = (
    <SelectField
      name="parkingCostType"
      label={COPY.parking.costUnitLabel}
      options={COST_UNIT_OPTIONS}
      dataCy="missing-info-parking-cost-unit"
      flex
    />
  );

  return (
    <>
      {isAsked('timePerPatient') && (
        <Section
          overline={COPY.timePerPatient.overline}
          helper={COPY.timePerPatient.helper}
          dataCy="missing-info-time-per-patient-section"
        >
          <SelectField
            name="timePerPatient"
            label={professionCode}
            options={TIME_PER_PATIENT_OPTIONS}
            dataCy="missing-info-time-per-patient"
          />
        </Section>
      )}

      {isAsked('assistedHygiene') && (
        <Section
          overline={COPY.assistedHygiene.overline}
          helper={COPY.assistedHygiene.helper}
          dataCy="missing-info-assisted-hygiene-section"
        >
          <YesNoField
            name="isAssistedHygiene"
            label={COPY.assistedHygiene.label}
            dataCy="missing-info-assisted-hygiene"
          />
        </Section>
      )}

      {isParkingAsked(missingFields) && (
        <Section overline={COPY.parking.overline} dataCy="missing-info-parking-section">
          {isAsked('parkingType') && (
            <SelectField
              name="parkingType"
              label={COPY.parking.typeLabel}
              options={parkingTypeOptions}
              dataCy="missing-info-parking-type"
            />
          )}
          {isAsked('parkingCost') && (
            <YesNoField
              name="isParkingFree"
              label={COPY.parking.isFreeLabel}
              dataCy="missing-info-parking-is-free"
            />
          )}
          {showCostFields && (
            <Row gap={4} align="start">
              <Controller
                name="parkingCost"
                control={control}
                render={({ field, fieldState }) => (
                  <Box flex>
                    <InputNumberField
                      data-cy="missing-info-parking-cost"
                      aria-label={COPY.parking.costLabel}
                      placeholder={COPY.parking.costLabel}
                      leftIcon={
                        <Typography variant="body1" color={colors.text.secondary}>
                          US$
                        </Typography>
                      }
                      value={field.value}
                      name={field.name}
                      ref={field.ref}
                      onBlur={field.onBlur}
                      onChange={(event) => {
                        const next = event.target.value;
                        if (next === '' || PARKING_COST_PATTERN.test(next)) {
                          field.onChange(next);
                        }
                      }}
                      isError={Boolean(fieldState.error)}
                    />
                  </Box>
                )}
              />
              {costUnitField}
            </Row>
          )}
          {showCostUnitOnly && costUnitField}
        </Section>
      )}

      {isAsked('softwares') && (
        <Section overline={COPY.softwares.overline} dataCy="missing-info-softwares-section">
          <Controller
            name="softwares"
            control={control}
            render={({ field, fieldState }) => (
              <CheckboxPillGroup
                data-cy="missing-info-softwares"
                options={softwareOptions}
                value={field.value}
                onChange={field.onChange}
                hasError={Boolean(fieldState.error)}
              />
            )}
          />
        </Section>
      )}

      {isAsked('insurances') && (
        <Section overline={COPY.insurances.overline} dataCy="missing-info-insurances-section">
          <Controller
            name="insurances"
            control={control}
            render={({ field, fieldState }) => (
              <CheckboxPillGroup
                data-cy="missing-info-insurances"
                options={insuranceOptions}
                value={field.value}
                onChange={field.onChange}
                hasError={Boolean(fieldState.error)}
              />
            )}
          />
        </Section>
      )}

      {isAsked('operatories') && (
        <Section overline={COPY.operatories.overline} dataCy="missing-info-operatories-section">
          <SelectField
            name="operatories"
            label={COPY.operatories.label}
            options={OPERATORIES_OPTIONS}
            dataCy="missing-info-operatories"
          />
        </Section>
      )}
    </>
  );
};
